import { type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { SCREENS } from 'services/Analytics/Analytics'
import useHomePageAnalytics from '../HomePage/useHomePageAnalytics'
import useHomePageCta from '../HomePage/useHomePageCta'
import {
  DESKTOP_NEW_PRODUCT_ITEMS,
  MULTI_PRODUCT_NAV_STATE,
  NEW_PRODUCT_ITEMS,
  type CtaAction,
  type CtaProduct,
  type NewProductConfig,
} from './multiProductHomePage.consts'
import { type NewProductItem } from './Components/NewProductsGrid/NewProductsGrid'

const defaultNavOptions = { state: MULTI_PRODUCT_NAV_STATE }

export default function useMultiProductHomePage() {
  const { t } = useTranslation()
  const { trackMainCta, trackSecondaryCta } = useHomePageAnalytics({
    screenName: SCREENS.HOMEPAGE,
    eventProperties: { 'Homepage Type': 'Multi Product' },
  })
  const { handleCtaLinkClick } = useHomePageCta()

  const trackCta = (product: CtaProduct, action: CtaAction) => {
    if (action === 'Buy' || action === 'Continue Design') {
      trackMainCta({ Product: product, Action: action })
    } else {
      trackSecondaryCta({ Product: product, Action: action })
    }
  }

  const onCtaClick = (
    event: MouseEvent<HTMLElement>,
    {
      url,
      action,
      product,
      requireQuiz = false,
    }: {
      url: string
      action: CtaAction
      product: CtaProduct
      requireQuiz?: boolean
    }
  ) => {
    trackCta(product, action)
    handleCtaLinkClick(event, {
      url,
      withQuiz: requireQuiz,
      quizNavOptions: defaultNavOptions,
    })
  }

  const toNewProductItem = (item: NewProductConfig): NewProductItem => ({
    label: t(item.labelKey),
    alt: item.alt,
    imageUrl: item.imageUrl,
    to: item.url,
    external: item.external,
    onClick: (e) =>
      onCtaClick(e, {
        url: item.url,
        action: 'Explore',
        product: item.alt,
        requireQuiz: true,
      }),
  })

  const newProductItems = NEW_PRODUCT_ITEMS.map(toNewProductItem)
  const desktopNewProductItems = DESKTOP_NEW_PRODUCT_ITEMS.map(toNewProductItem)

  return { onCtaClick, newProductItems, desktopNewProductItems }
}
